/**
 * ScrapeArbitrageModal
 * Modal d'arbitrage après échec partiel du scraping
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';

export const ScrapeArbitrageModal = ({
  scrapeModal,
  starting,
  onCancel,
  onContinueWithExisting,
  onAddUrlsAndRetry,
}) => {
  const [addUrlsValue, setAddUrlsValue] = useState('');

  if (!scrapeModal) return null;

  const handleAddUrlsAndRetry = () => {
    const newUrls = addUrlsValue.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    onAddUrlsAndRetry(newUrls, () => setAddUrlsValue(''));
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="scrape-modal">
      <Card className="border-amber-500/50 bg-slate-900 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader>
          <CardTitle className="text-sm text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Échec partiel du scraping ({scrapeModal.failed}/{scrapeModal.total} URL{scrapeModal.total > 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 overflow-y-auto flex-1">
          <div className="text-xs text-slate-300">
            Certaines URLs sources n&apos;ont pas pu être scrapées. La génération est en pause —
            choisissez comment poursuivre.
          </div>

          {/* Liste des sources */}
          <div className="space-y-1 max-h-[280px] overflow-y-auto bg-slate-950/50 rounded p-2">
            {scrapeModal.sources.map((s, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-2 p-2 rounded text-xs border ${
                  s.success
                    ? 'bg-emerald-950/30 border-emerald-700/30'
                    : 'bg-red-950/30 border-red-700/40'
                }`}
                data-testid={`scrape-row-${idx}`}
              >
                <span className="font-mono mt-0.5">
                  {s.success ? '✅' : '❌'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-200 truncate font-mono">{s.url}</div>
                  {s.success ? (
                    <div className="text-emerald-300/80 text-[10px] mt-0.5">
                      {s.title || '—'} · {s.char_count || 0} chars
                    </div>
                  ) : (
                    <div className="text-red-300/80 text-[10px] mt-0.5">
                      {s.error || 'Erreur inconnue'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Champ pour ajouter de nouvelles URLs */}
          <div>
            <label className="text-xs text-slate-400 block mb-1">
              Ajouter de nouvelles URLs (1 par ligne ou séparées par virgule)
            </label>
            <Textarea
              value={addUrlsValue}
              onChange={e => setAddUrlsValue(e.target.value)}
              rows={3}
              placeholder={"https://...\nhttps://..."}
              className="font-mono text-xs"
              data-testid="scrape-add-urls-input"
            />
          </div>
        </CardContent>
        <div className="border-t border-slate-700 p-3 flex flex-wrap justify-end gap-2">
          <Button
            onClick={() => { onCancel(); setAddUrlsValue(''); }}
            variant="outline"
            size="sm"
            data-testid="scrape-cancel-btn"
          >
            Annuler
          </Button>
          {scrapeModal.succeeded > 0 && (
            <Button
              onClick={onContinueWithExisting}
              disabled={starting || addUrlsValue.trim().length > 0}
              className="bg-emerald-600 hover:bg-emerald-500"
              size="sm"
              data-testid="scrape-continue-btn"
            >
              Continuer avec {scrapeModal.succeeded} source{scrapeModal.succeeded > 1 ? 's' : ''}
            </Button>
          )}
          <Button
            onClick={handleAddUrlsAndRetry}
            disabled={starting || addUrlsValue.trim().length === 0}
            className="bg-cyan-600 hover:bg-cyan-500"
            size="sm"
            data-testid="scrape-add-retry-btn"
          >
            {starting ? 'Scraping…' : 'Ajouter URLs & re-scraper'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ScrapeArbitrageModal;
